import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { createKartverketTopoLayer } from '../lib/mapTiles';
import {
  leafletLatLngsToRing,
  locksInsidePolygon,
  ringToGeoJsonPolygon
} from '../lib/geo';
import { dataverseService } from '../services/dataverseService';
import { getDataverseErrorMessage } from '../lib/dataverseErrorHandler';

function hasCoordinates(lock) {
  return typeof lock.latitude === 'number' && typeof lock.longitude === 'number';
}

function destroyLeafletMap(mapRef, extraRefs = []) {
  const map = mapRef.current;
  if (!map) {
    extraRefs.forEach((ref) => {
      if (ref) ref.current = null;
    });
    return;
  }
  mapRef.current = null;
  try {
    map.stop();
    map.off();
    map.remove();
  } catch {
    // Container may already be gone with the dialog.
  }
  extraRefs.forEach((ref) => {
    if (ref) ref.current = null;
  });
}

/**
 * @param {{ open: boolean, onClose: () => void, locks: any[], center: { lat: number, lng: number }, onCreated: () => void }} props
 */
export default function CreateAccessGroupFromMapDialog({
  open,
  onClose,
  locks = [],
  center,
  onCreated
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const drawControlRef = useRef(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultForLocks, setDefaultForLocks] = useState(false);
  const [defaultForPhones, setDefaultForPhones] = useState(false);
  const [mapPolygon, setMapPolygon] = useState(null);
  const [selectedLocks, setSelectedLocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const mappableLocks = useMemo(
    () => locks.filter((lock) => hasCoordinates(lock)),
    [locks]
  );
  const withoutCoords = locks.length - mappableLocks.length;

  const handleClose = () => {
    destroyLeafletMap(mapRef, [markersLayerRef, drawnItemsRef, drawControlRef]);
    onClose?.();
  };

  useEffect(() => {
    if (!open) {
      destroyLeafletMap(mapRef, [markersLayerRef, drawnItemsRef, drawControlRef]);
      return undefined;
    }

    setName('');
    setDescription('');
    setDefaultForLocks(false);
    setDefaultForPhones(false);
    setMapPolygon(null);
    setSelectedLocks([]);
    setError(null);
    setSaving(false);

    let cancelled = false;

    const timer = setTimeout(() => {
      if (cancelled || !mapContainerRef.current) {
        return;
      }

      destroyLeafletMap(mapRef, [markersLayerRef, drawnItemsRef, drawControlRef]);

      const map = L.map(mapContainerRef.current, { scrollWheelZoom: true });
      createKartverketTopoLayer().addTo(map);
      map.setView([center?.lat || 59.91, center?.lng || 10.75], 12);

      const markersLayer = L.layerGroup().addTo(map);
      const drawnItems = new L.FeatureGroup().addTo(map);
      markersLayerRef.current = markersLayer;
      drawnItemsRef.current = drawnItems;

      const drawControl = new L.Control.Draw({
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: true
          },
          polyline: false,
          rectangle: false,
          circle: false,
          circlemarker: false,
          marker: false
        },
        edit: {
          featureGroup: drawnItems,
          remove: true
        }
      });
      map.addControl(drawControl);
      drawControlRef.current = drawControl;

      const applyPolygonFromLayer = (layer) => {
        const ring = leafletLatLngsToRing(layer.getLatLngs());
        const geo = ringToGeoJsonPolygon(ring);
        setMapPolygon(geo);
        setSelectedLocks(locksInsidePolygon(mappableLocks, geo));
      };

      map.on(L.Draw.Event.CREATED, (event) => {
        drawnItems.clearLayers();
        drawnItems.addLayer(event.layer);
        applyPolygonFromLayer(event.layer);
      });

      map.on(L.Draw.Event.EDITED, (event) => {
        event.layers.eachLayer((layer) => applyPolygonFromLayer(layer));
      });

      map.on(L.Draw.Event.DELETED, () => {
        setMapPolygon(null);
        setSelectedLocks([]);
      });

      mapRef.current = map;
      requestAnimationFrame(() => {
        if (mapRef.current === map) {
          map.invalidateSize();
        }
      });
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      destroyLeafletMap(mapRef, [markersLayerRef, drawnItemsRef, drawControlRef]);
    };
  }, [open, center, mappableLocks]);

  useEffect(() => {
    const markersLayer = markersLayerRef.current;
    const map = mapRef.current;
    if (!open || !markersLayer || !map) {
      return;
    }

    markersLayer.clearLayers();
    const selectedIds = new Set(selectedLocks.map((lock) => lock.id || lock.lockId));

    mappableLocks.forEach((lock) => {
      const selected = selectedIds.has(lock.id || lock.lockId);
      const marker = L.circleMarker([lock.latitude, lock.longitude], {
        radius: selected ? 9 : 7,
        color: selected ? '#1565c0' : '#2e7d32',
        weight: 2,
        fillColor: selected ? '#1976d2' : '#2e7d32',
        fillOpacity: 0.85
      });
      marker.bindTooltip(lock.name || lock.lockId, { direction: 'top', offset: [0, -8] });
      marker.addTo(markersLayer);
    });

    if (mappableLocks.length > 1 && !mapPolygon) {
      const bounds = L.latLngBounds(mappableLocks.map((l) => [l.latitude, l.longitude]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [open, mappableLocks, selectedLocks, mapPolygon]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Navn er påkrevd.');
      return;
    }
    if (!mapPolygon) {
      setError('Tegn en sone (polygon) på kartet.');
      return;
    }
    if (selectedLocks.length === 0) {
      setError('Sonen inneholder ingen Unloc-låser med posisjon.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const group = await dataverseService.accessGroupService.create({
        name: name.trim(),
        description,
        defaultForLocks,
        defaultForPhones,
        mapPolygon
      });
      await dataverseService.accessGroupService.setMembership(group.id, {
        lockIds: selectedLocks.map((lock) => lock.id || lock.lockId),
        phoneIds: []
      });
      destroyLeafletMap(mapRef, [markersLayerRef, drawnItemsRef, drawControlRef]);
      onCreated?.();
      onClose();
    } catch (saveError) {
      setError(getDataverseErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      disableRestoreFocus
      TransitionProps={{
        onExited: () =>
          destroyLeafletMap(mapRef, [markersLayerRef, drawnItemsRef, drawControlRef])
      }}
    >
      <DialogTitle>Opprett tilgangsgruppe fra kart</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
            gap: 2,
            minHeight: { xs: 480, md: 560 }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              label="Navn"
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Beskrivelse"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={defaultForLocks}
                  onChange={(event) => setDefaultForLocks(event.target.checked)}
                />
              }
              label="Default for nye låser"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={defaultForPhones}
                  onChange={(event) => setDefaultForPhones(event.target.checked)}
                />
              }
              label="Default for nye telefoner"
            />
            <Typography variant="subtitle2">
              Valgte låser ({selectedLocks.length})
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 120,
                maxHeight: 220,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1
              }}
            >
              {selectedLocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Tegn et polygon for å velge låser.
                </Typography>
              ) : (
                selectedLocks.map((lock) => (
                  <Typography key={lock.id || lock.lockId} variant="body2" sx={{ mb: 0.5 }}>
                    {lock.name}
                  </Typography>
                ))
              )}
            </Box>
            {withoutCoords > 0 && (
              <Alert severity="info">
                {withoutCoords} Unloc-lås(er) mangler posisjon og kan ikke velges via kart.
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>

          <Box
            ref={mapContainerRef}
            sx={{
              height: { xs: 320, md: '100%' },
              minHeight: 320,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Avbryt
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Oppretter…' : 'Opprett gruppe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

